import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import axios from "axios"
import { Users, FileWarning, Send, ShieldCheck, Plus } from "lucide-react"
import AppLayout from "@/components/app/AppLayout"
import { Button } from "@/components/ui/button"
import { API_BASE } from "@/config/api"
import { HrSubnav } from "./HrSubnav"

const API = API_BASE

export default function HrDashboard() {
  const { t } = useTranslation()
  const [data, setData] = useState(null)

  useEffect(() => {
    axios.get(`${API}/hr/dashboard`).then((r) => setData(r.data)).catch(() => {})
  }, [])

  const c = data?.compliance || {}

  return (
    <AppLayout
      title={t("app.hr.title")}
      actions={
        <Button asChild size="sm">
          <Link to="/app/hr/employees?new=1">
            <Plus className="h-4 w-4 mr-1" />
            {t("app.hr.newHire")}
          </Link>
        </Button>
      }
    >
      <HrSubnav />
      <p className="text-sm text-muted-foreground mb-6">{t("app.hr.intro")}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Metric icon={Users} label={t("app.hr.metrics.activeEmployees")} value={data?.active_employees ?? "—"} />
        <Metric icon={Send} label={t("app.hr.metrics.pendingReges")} value={data?.pending_reges ?? "—"} />
        <Metric icon={FileWarning} label={t("app.hr.metrics.pendingLeave")} value={data?.pending_leave ?? "—"} />
        <Metric icon={ShieldCheck} label={t("app.hr.metrics.itmCompliance")} value={`${c.score ?? 0}%`} highlight={c.ready_for_itm} />
      </div>
      {c.issues?.length > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 mb-6">
          <p className="text-sm font-medium mb-2">{t("app.hr.itmIssuesTitle")}</p>
          <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
            {c.issues.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </div>
      )}
      {data?.expiring_documents?.length > 0 && (
        <div className="rounded-xl border border-border p-4">
          <p className="text-sm font-medium mb-3">{t("app.hr.expiringDocsTitle")}</p>
          <ul className="text-sm space-y-2">
            {data.expiring_documents.map((d) => (
              <li key={d.id} className="flex justify-between">
                <span>
                  {d.first_name} {d.last_name} — {d.doc_key}
                </span>
                <span className="text-muted-foreground">{d.expires_at?.slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </AppLayout>
  )
}

function Metric({ icon: Icon, label, value, highlight }) {
  return (
    <div
      className={`rounded-xl border p-4 ${highlight ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}
    >
      <Icon className="h-5 w-5 text-muted-foreground mb-2" />
      <p className="text-2xl font-display font-medium">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  )
}
